import { usePopover } from '@deltasierra/react-hooks';
import { TextFieldProps, InputProps } from '@material-ui/core';
import CalendarToday from '@material-ui/icons/CalendarToday';
import * as React from 'react';
import { DSPopover } from '../mui/Popover';
import { Calendar } from '../Calendar';
import { DSIconButton } from '../Generic/Buttons/DSIconButton';
import { DSTextField } from '../mui/TextField';
import DateRangeTuple from './date-range-tuple.type';
import { useFormatDates } from './hooks/use-format-dates';
import { useToggledSetDate } from './hooks/use-toggled-set-date';

export interface NewDateRangePickerProps {
    children?: React.ReactNode;
    className?: string;
    label?: string;
    style?: React.CSSProperties;
    value: DateRangeTuple;
    variant?: TextFieldProps['variant'];
    fullWidth?: TextFieldProps['fullWidth'];
    onChange?: (value: DateRangeTuple) => void;
    inputPropsClasses?: InputProps['classes'];
}

export function NewDateRangePicker(props: NewDateRangePickerProps): JSX.Element {
    const [selected, selectedTo] = props.value;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const displayValue = useFormatDates(selected, selectedTo);
    const [anchorEl, isOpen, { handleClose, handleToggle }] = usePopover<HTMLInputElement>();
    const [setDate, isSelectingTo] = useToggledSetDate(props.value, props.onChange);

    return (
        <div>
            <DSTextField
                InputProps={{
                    classes: props.inputPropsClasses,
                    endAdornment: (
                        <DSIconButton aria-label="calendar-picker" size="small">
                            <CalendarToday />
                        </DSIconButton>
                    ),
                    style: { cursor: 'pointer' },
                }}
                className={props.className}
                fullWidth={props.fullWidth}
                inputRef={inputRef}
                label={props.label}
                style={{ cursor: 'pointer', ...props.style }}
                tabIndex={-1}
                value={displayValue}
                variant={props.variant}
                onClick={handleToggle}
            />
            <DSPopover
                PaperProps={{ style: { marginTop: '16px' } }}
                anchorEl={anchorEl}
                anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'bottom',
                }}
                open={isOpen}
                transformOrigin={{
                    horizontal: 'right',
                    vertical: 'top',
                }}
                onClose={handleClose}
            >
                <div role="dialog" style={{ display: 'flex', flexDirection: 'row' }}>
                    <div style={{ maxWidth: '380px', padding: '12px' }}>
                        <Calendar
                            isSelectingTo={isSelectingTo}
                            selected={selected}
                            selectedTo={selectedTo}
                            onSelectDate={setDate}
                        >
                            <Calendar.MonthHeader />
                            <Calendar.Month />
                        </Calendar>
                    </div>
                    {props.children && <div style={{ borderLeft: '1px solid lightgray' }}>{props.children}</div>}
                </div>
            </DSPopover>
        </div>
    );
}
NewDateRangePicker.displayName = 'NewDateRangePicker';
